import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useAuth } from '@clerk/react';

import { createApiClient, type ApiClient } from './api-client';

const ApiContext = createContext<ApiClient | null>(null);

/**
 * Wraps Clerk's `getToken` into a single shared `ApiClient` instance
 * for the app. Mounted in `App.tsx` above `<ChatWorkspace>`.
 */
export function ApiProvider({ children }: { children: ReactNode }) {
  const { getToken } = useAuth();
  const client = useMemo(
    () =>
      createApiClient({
        getToken: async () => {
          try {
            return await getToken();
          } catch {
            return null;
          }
        },
      }),
    [getToken],
  );
  return <ApiContext.Provider value={client}>{children}</ApiContext.Provider>;
}

/** Read the shared `ApiClient`. Throws if used outside `<ApiProvider>`. */
export function useApi(): ApiClient {
  const ctx = useContext(ApiContext);
  if (!ctx) {
    throw new Error('useApi must be used inside <ApiProvider>');
  }
  return ctx;
}
